import { Component } from '@angular/core';
import { IUser } from '../../../shared/models/user';
import { AuthService } from '../../../shared/services/auth.service';
import { Subscription } from 'rxjs';
import { IConnectLinkItem } from '../shared/models/connect-link-item';
import { IBlobOverview } from '../../../shared/models/blob-overview';
import { GuidesAndVideosService } from '../shared/services/guides-and-videos.service';
import { IBlobLinkItem } from '../shared/models/blob-link-item';
import { IApiResponse } from '../../../shared/models/api-response';
import { IBlobFile } from '../../../shared/models/blob-file';
import { FileDownloadUtility } from '../../../shared/utilities/blob-download-utility';
import lodashUtility from 'lodash';
import { UserUtility } from '../../../shared/utilities/user-utility';


@Component({
    selector: 'app-guides-and-videos',
    templateUrl: './guides-and-videos.component.html',
    styleUrl: './guides-and-videos.component.scss',
    standalone: false
})
export class GuidesAndVideosComponent {

  currentUser: IUser | null = null;
  isCm: boolean = false;
  isInternalUser: boolean = false;
  subscription: Subscription = new Subscription();

  generalLinks: (IConnectLinkItem | IBlobLinkItem)[] = [];
  generalBlobLinks: IBlobLinkItem[] = [];
  committeeWorkspaceLinks: IBlobLinkItem[] = [];
  systematicReviewLinks: IBlobLinkItem[] = [];
  projectProposalPortalLinks: IBlobLinkItem[] = [];
  draftingToolsLinks: IConnectLinkItem[] = [];

  asIso24495Id: string = 'btn-asiso24495-document-download';
  asIso24495Designation: string = 'AS ISO 24495.1:2024';
  asIso24495Title: string = 'AS ISO 24495.1:2024 Plain language, Part 1: Governing principles and guidelines';

  constructor(
    private _authService: AuthService,
    private _guidesAndVideosService: GuidesAndVideosService
  ) { }

  ngOnInit(): void {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      });

      this.initialise();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }


  initialise() {
    this.subscription.add(this._authService.currentUserSubject.subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isCm = UserUtility.isCommitteeMember(user);
        this.isInternalUser = UserUtility.isInternalUser(user);
        this.draftingToolsLinks = this.getDraftingToolsLinks();
      },
      error: (error) => {
        // Log to sumologic
      }
    }));

    const blobOverviewSubscription = this._guidesAndVideosService.GetUserGuideAndVideoContainerContents().subscribe({
      next: (response) => {
        if (response.success && response.content && response.content.length > 0) {
            this.generalLinks = this.getGeneralLinks(response.content);
            this.generalBlobLinks = this.generalLinks.filter(x => (x as IBlobLinkItem).blobOverview) as IBlobLinkItem[];
            this.committeeWorkspaceLinks = this.getCommitteeWorkspaceLinks(response.content);
            this.projectProposalPortalLinks = this.getProjectProposalPortalLinks(response.content);
        } else {
          // Log to sumologic
        }
      },
      error: (error) => {
        // Log to sumologic
      }
    });

    this.subscription.add(blobOverviewSubscription);


  }

  private getGeneralLinks(blobOverviews: IBlobOverview[]): (IConnectLinkItem | IBlobLinkItem)[] {
    return [
      {
        id: 'btn-committee-member-guide',
        displayText: 'Committee Member Guide',
        href: 'https://www.standards.org.au/documents/committee-member-guide',
        routerLink: '',
        action: false,
        show: true
      },
      {
        id: 'btn-how-to-participate',
        displayText: 'Drafting Resources',
        href: 'https://www.standards.org.au/standards-development/developing-standards/participate',
        routerLink: '',
        action: false,
        show: true
      },
      {
        id: 'btn-standardisation-guides',
        displayText: 'Rules for the Structure and Drafting of Australian Standards® (SG-006)',
        href: 'https://www.standards.org.au/standardisation-guides',
        routerLink: '',
        action: false,
        show: true
      },
      {
        id: 'btn-connect-help-user-guide-for-committee-members',
        displayText: 'Connect Help Guide for Committee Members',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'ConnectHelpGuideForCommitteeMembers.pdf'.toLowerCase()) || null
      },
      {
        id: 'btn-standards-library-user-guide',
        displayText: 'Standards Library User Guide for Government - PDF',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'StandardsLibraryUserGuideForGovernment.pdf'.toLowerCase()) || null
      }
    ];
  }

  getCommitteeWorkspaceLinks(blobOverviews: IBlobOverview[]): IBlobLinkItem[] {
    const committeeWorkspaceLinks: IBlobLinkItem[] = [
      {
        id: 'btn-training-guide-for-committee-members',
        displayText: 'Training Guide for Committee Members - PDF',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserGuide_01.pdf'.toLowerCase()) || null
      },
      {
        id: 'btn-overview',
        displayText: 'Overview - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_01.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-login-logout',
        displayText: 'Log In Log Out - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_02.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-using-filters',
        displayText: 'Using Filters - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_03.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-uploading-document-to-members-area-folder',
        displayText: "Uploading a Document to the Member's Area Folder - Video",
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_04.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-adding-folders-to-favourites',
        displayText: 'Adding Folders to Favourites - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_05.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-adding-documents-to-favourites',
        displayText: 'Adding Documents to Favourites - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_06.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-downloading-a-document',
        displayText: 'Downloading a Document - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_07.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-adding-notes',
        displayText: 'Adding Notes - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_08.mp4'.toLowerCase()) || null
      },
      {
        id: 'btn-printing-documents',
        displayText: 'Printing Documents - Video',
        href: '',
        routerLink: '',
        action: true,
        show: true,
        blobOverview: blobOverviews.find(x => x.blobName.toLowerCase() === 'CommitteeWorkspaceUserVideo_09.mp4'.toLowerCase()) || null
      },
    ];

    return committeeWorkspaceLinks;
  }

  private getProjectProposalPortalLinks(blobOverviews: IBlobOverview[]): IBlobLinkItem[] {
    return blobOverviews.filter(x => x.blobName.toLocaleLowerCase().includes("project proposal portal"))
      .map(blobOverview => {
        let blobLinkName = blobOverview?.blobName?.split(".")[2];
        return {
          id: `btn-${lodashUtility.kebabCase(blobLinkName)}`,
          displayText: blobLinkName,
          href: '',
          routerLink: '',
          action: true,
          show: true,
          blobOverview
        }
      }
      );
  }

  private getDraftingToolsLinks(): IConnectLinkItem[] {

    const draftingToolsLinks: IConnectLinkItem[] = [
      {
        id: this.asIso24495Id,
        displayText: this.asIso24495Title,
        href: '',
        routerLink: '',
        action: true,
        show: this.isCm || this.isInternalUser,
      }
    ];

    return draftingToolsLinks;
  }

  public onActionClicked(linkItem: IConnectLinkItem) : void {
    const allLinks : IBlobLinkItem[]  = [...this.generalBlobLinks,
                                         ...this.committeeWorkspaceLinks,
                                         ...this.systematicReviewLinks,
                                         ...this.projectProposalPortalLinks];

    const blobOverview =  allLinks.filter(x => x.id === linkItem.id)[0]?.blobOverview?? null;

    if (blobOverview) {
      const subscription = this._guidesAndVideosService.DownloadUserGuideAndVideo(blobOverview.containerName, blobOverview.blobName)
        .subscribe({
          next: (response: IApiResponse<IBlobFile>) => {
            if(response.success && response.content) {
              const blobFile = response.content;
              FileDownloadUtility.downloadBlobToBrowser(blobFile);
            }
          },
          error: (error) => {
            // Log to sumologic
          }
      });

      this.subscription.add(subscription);
    }
  }

  public onDraftingToolsActionClicked(linkItem: IConnectLinkItem) : void {
    if(linkItem.id === this.asIso24495Id) {
      const fileName = this. asIso24495Title + '.pdf';
      this._guidesAndVideosService.DownloadAsIso24495Document().subscribe({
        next: (response: IApiResponse<string>) => {
          if(response.success && response.content) {
            FileDownloadUtility.downloadbase64StringToBrowser(response.content, fileName);
          } else {
            console.log('Failed to download the document. Please try again later.');
          }
        },
        error: (error) => {
          console.log('Error downloading the document:', error);
        }
      });
    }
  }
}

